import { useRef, useState } from "react";
import type { Member, MemberAccountTransaction, PaymentMode } from "@/services/types";
import { memberService } from "@/services/members";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Select } from "@/components/base/Select";
import { TextField } from "@/components/base/TextField";
import { Textarea } from "@/components/base/Textarea";
import { DatePicker } from "@/components/base/DatePicker";
import { CurrencyInput } from "@/components/currency/CurrencyInput";
import { Badge } from "@/components/base/Badge";
import { useToast } from "@/context/ToastContext";

interface EditAccountTransactionModalProps {
  open: boolean;
  onClose: () => void;
  member: Member;
  transaction: MemberAccountTransaction;
  onSuccess: (transaction: MemberAccountTransaction, updatedMember: Member) => void;
}

const MODES: { value: PaymentMode; label: string }[] = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "adjustment", label: "Adjustment" },
];

function extractDateOnly(dateStr?: string): string {
  if (!dateStr) {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) {
    return dateStr.slice(0, 10);
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function EditAccountTransactionModal({
  open,
  onClose,
  member,
  transaction,
  onSuccess,
}: EditAccountTransactionModalProps) {
  const toast = useToast();
  const [amount, setAmount] = useState<number | null>(Number(transaction.amount) || null);
  const [mode, setMode] = useState<PaymentMode>((transaction.mode ? transaction.mode.toLowerCase() : "cash") as PaymentMode);
  const [referenceNo, setReferenceNo] = useState(transaction.referenceNo || "");
  const [date, setDate] = useState<string>(extractDateOnly(transaction.createdAt));
  const [remarks, setRemarks] = useState(transaction.remarks || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  const isLinked = Boolean(transaction.paymentId || transaction.payoutId);
  const referenceNeeded = mode !== "cash" && mode !== "adjustment";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!isLinked && (!amount || amount <= 0)) {
      setError("Please enter a valid amount greater than zero.");
      return;
    }
    if (referenceNeeded && !referenceNo.trim()) {
      setError("Reference number is required for non-cash modes.");
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const res = await memberService.updateAccountTransaction(member.id, transaction.id, {
        amount: isLinked ? undefined : (amount ?? undefined),
        mode,
        referenceNo: referenceNo.trim() || undefined,
        date,
        remarks: remarks.trim() || undefined,
      });

      toast.success(
        "Transaction updated",
        `Entry ${transaction.receiptNo || transaction.id.slice(0, 8)} was updated and ledger recalculated.`
      );
      onSuccess(res.transaction, res.member);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update transaction.");
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Account Transaction"
      description={`Update transaction details for ${member.fullName}.`}
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<i className="ri-check-line" />}
            loading={loading}
            onClick={handleSubmit}
          >
            Save Changes
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error ? (
          <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-xs text-danger-700">
            {error}
          </div>
        ) : null}

        {/* Info Banner */}
        <div className="rounded-lg border border-background-200 bg-background-100/50 p-3 text-xs text-foreground-700 flex items-center justify-between">
          <div>
            <div className="font-semibold text-foreground-900 flex items-center gap-2">
              <span>Receipt / Voucher:</span>
              <span className="font-mono text-primary-700 font-bold">{transaction.receiptNo || "—"}</span>
            </div>
            <div className="text-foreground-500 mt-0.5">
              Type: <Badge color="neutral">{transaction.type.replace(/_/g, " ")}</Badge>
            </div>
          </div>
          <div className="text-right text-[11px] text-foreground-500">
            Passbook ledger will auto-recalculate
          </div>
        </div>

        {isLinked ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 flex items-start gap-2">
            <i className="ri-information-line text-amber-600 mt-0.5" />
            <span>
              This entry is linked to an installment payment or payout. Amount cannot be modified directly here.
            </span>
          </div>
        ) : null}

        <div>
          <CurrencyInput
            label="Amount"
            value={amount}
            onChange={setAmount}
            placeholder="0.00"
            disabled={isLinked}
            required={!isLinked}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label="Payment Mode"
            value={mode}
            onChange={(v) => setMode(v as PaymentMode)}
            options={MODES}
          />
          <TextField
            label={referenceNeeded ? "Reference Number" : "Reference (Optional)"}
            placeholder={referenceNeeded ? "e.g. UTR / Ref #" : "Optional"}
            value={referenceNo}
            onChange={(e) => setReferenceNo(e.target.value)}
          />
        </div>

        <DatePicker label="Transaction Date" value={date} onChange={setDate} />

        <Textarea
          label="Remarks (Optional)"
          placeholder="Notes or reason for change"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          rows={2}
        />
      </form>
    </Modal>
  );
}
